import snapshot from "./api.json";
import { groupTitle } from "./content";

export { groupTitle };

export type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";

export type ParamLocation = "path" | "query" | "body";

export type ApiParam = {
  name: string;
  depth: number;
  in: ParamLocation;
  type: string;
  required: boolean;
  description: string;
  constraints: string | null;
  default: string | number | boolean | null;
  options: string[] | null;
  example: string | number | boolean | null;
};

export type ApiError = { status: number; code: string; when: string };

export type ApiEndpoint = {
  id: string;
  group: string;
  method: HttpMethod;
  path: string;
  title: string;
  description: string;
  notes: string[];
  credits: string;
  rateTier: string;
  params: ApiParam[];
  sampleUrl: string;
  sampleBody: Record<string, Json> | null;
  response: Json;
  errors: ApiError[];
  curl: string;
};

export type ApiGroup = {
  id: string;
  title: string;
  description: string;
  section: string | null;
  endpointIds: string[];
};

export type RateTier = { label: string; perSecond: number; perMinute: number };

export type ObjectField = {
  name: string;
  type: string;
  description: string;
  children?: ObjectField[];
};

export type SharedObject = {
  id: string;
  schema: string;
  title: string;
  description: string;
  fields: ObjectField[];
  example: Json;
};

export type WebhookEvent = { id: string; description: string };

export type CreditType = { id: string; label: string; spentOn: string };

export type ApiSnapshot = {
  generatedAt: string;
  baseUrl: string;
  apiKeyPrefix: string;
  webhookSecretPrefix: string;
  maxApiKeys: number;
  apiKeyExpiries: { id: string; label: string }[];
  defaultApiKeyExpiry: string;
  discordUrl: string;
  supportEmail: string;
  viewTracker: {
    title: string;
    chooser: string;
    limits: Record<string, number | string[]>;
    rateTiers: { tier: string; appliesTo: string }[];
  };
  rateTiers: Record<string, RateTier>;
  generalRateTiers: string[];
  limits: Record<string, number> & { labels: Record<string, string> };
  allowedMimeTypes: Record<string, string[]>;
  creditTypes: CreditType[];
  webhookEvents: WebhookEvent[];
  trackerWebhookPayloads: { event: string; data: Json }[];
  sharedErrors: ApiError[];
  errorIndex: { status: number; code: string; endpoints: string[] }[];
  groups: ApiGroup[];
  endpoints: ApiEndpoint[];
  sharedObjects: SharedObject[];
};

export const API = snapshot as unknown as ApiSnapshot;

export const API_BASE_URL = API.baseUrl;

export const OPENAPI_PATH = "/docs/openapi.json";

export const DEVELOPERS_URL = "https://crayo.ai/developers";

const endpointMap = new Map(API.endpoints.map((endpoint) => [endpoint.id, endpoint]));
const groupMap = new Map(API.groups.map((group) => [group.id, group]));

export const getGroup = (id: string) => groupMap.get(id);

export const getEndpoint = (id: string) => endpointMap.get(id);

export const endpointsInGroup = (groupId: string): ApiEndpoint[] =>
  API.endpoints.filter((endpoint) => endpoint.group === groupId);

export const rateTier = (id: string): RateTier => API.rateTiers[id];

export const endpointAnchor = (endpoint: ApiEndpoint) => `/docs/${endpoint.group}#${endpoint.id}`;

export const prettyJson = (value: Json) => JSON.stringify(value, null, 2);

export const endpointCount = API.endpoints.length;

export const groupCount = API.groups.length;
